import { Injectable } from '@angular/core';
import { userPermissions } from '../models/enums/user-permissions.enum';
import { ManagementApiService } from './management-api.service';
import { ErrorDialogComponent } from '../shared/info-dialogs/error-dialog/error-dialog.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FullUserData } from '../Modules/management/models/organization/fullUserData.model';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { UserSessions } from '../models/user-sessions-details';
import jwt_decode from 'jwt-decode';
import { UserPermissions } from '../Modules/management/models/organization/user-permissions.model';
import { GenerateUniqueIdService } from './generateuniqueid.service';

@Injectable({
    providedIn: 'root',
})
export class UserUtilityService {
    userData: FullUserData;
    userPermissions: UserPermissions[];
    noneSelectedPassPct: boolean = false;
    userSessionData = new BehaviorSubject<UserSessions>(null);
    errorPopupCount = 0;
    isLicenseChecked = false;
    destroy$ = new Subject();
    constructor(
        private _managementApiService: ManagementApiService,
        private modalService: NgbModal,
        private router: Router,
        private authService: MsalService,
        private genDeviceId: GenerateUniqueIdService
    ) {}

    getUserData(): FullUserData {
        return this.userData;
    }

    setUserData(user: FullUserData) {
        this.userData = user;
    }

    getUserSecurityRoleName() {
        return this.userData.securityRoles[0].securityRole.name;
    }

    isLicenseValid(): boolean {
        return this.userData.license.isValid;
    }

    checkUserPermissionOnRoute(path: string, action: string): boolean {
        const realPath = userPermissions[path];
        const license = this.userData?.license;

        if (!license?.isValid) {
            return false;
        }

        if (![1, 2, 3, 4, 5, 6, 7].includes(license.instanceTypeIndex)) {
            return false;
        }

        if (!this.userPermissions?.length) {
            return false;
        }

        return this.userPermissions.some((permission) => permission.path === realPath && permission.action === action && permission.allowed === true);
    }

    public validateSelectedOrgAndInstanceAccess(userDetailResponse?: any): boolean {
        if (!this.isLicenseChecked) {
            let selectedInsDetails = JSON.parse(localStorage.getItem('selectedInstanceDetails'));
            let selectedOrganization = JSON.parse(localStorage.getItem('selectedOrganization'));
            // userDetailResponse.license.isValid = false;
            if (selectedInsDetails && selectedOrganization) {
                this.isLicenseChecked = true;
                if (
                    (!selectedOrganization?.enabled || !selectedInsDetails?.enabled) &&
                    !userDetailResponse.license.isValid &&
                    userDetailResponse?.isSysAdmin != true
                ) {
                    this.errorModelPopupExpiry(
                        'Your license has expired and the access period for your ' +
                            (!selectedOrganization?.enabled
                                ? 'organization (' + selectedOrganization.name + ')'
                                : 'instance (' + selectedInsDetails.name + ')') +
                            ' has ended. Please contact your administrator for assistance.'
                    );
                    return false;
                } else if (
                    selectedOrganization?.enabled &&
                    selectedInsDetails?.enabled &&
                    !userDetailResponse.license.isValid &&
                    userDetailResponse?.isSysAdmin != true
                ) {
                    this.errorModelPopupExpiry('Your license has expired. Please contact your administrator for assistance.');
                    return false;
                } else if (
                    (!selectedOrganization?.enabled || !selectedInsDetails?.enabled) &&
                    userDetailResponse.license.isValid &&
                    userDetailResponse?.isSysAdmin != true
                ) {
                    this.errorModelPopupExpiry(
                        'The access period for your ' +
                            (!selectedOrganization?.enabled
                                ? 'organization (' + selectedOrganization.name + ')'
                                : 'instance (' + selectedInsDetails.name + ')') +
                            ' has ended. Please contact your administrator for assistance.'
                    );
                    return false;
                }

                this.userData = userDetailResponse;

                return true;
            } else if (!userDetailResponse.license.isValid && userDetailResponse?.isSysAdmin != true) {
                this.isLicenseChecked = true;
                this.errorModelPopupExpiry('Your license has expired. Please contact your administrator for assistance.');
                return false;
            }

            this.userData = userDetailResponse;
            return true;
        } else {
            return true;
        }
    }

    async checkUserPermissionDataAndRoute(path: string, action: string): Promise<boolean> {
        try {
            if (path == 'cdms-settings') {
                let tt = 0; // * Allow access to iDMS menu if user has view-idms-menu permission, without checking license or org/instance access
            }
            if (!this.userData) {
                const token = localStorage.getItem('msal.accessToken');
                const decodedToken: any = jwt_decode(token);
                const userDetailResponse = await this._managementApiService.getFullUserDetail(decodedToken.oid).toPromise();
                if (!userDetailResponse.success) {
                    if (this.errorPopupCount < 1) this.errorModelPopup(userDetailResponse.message);
                    this.errorPopupCount++;
                    return false;
                }
                this.userData = userDetailResponse.response;
                // if (!this.validateSelectedOrgAndInstanceAccess(userDetailResponse.response)) {
                //     return false;
                // }
            }

            if (!this.userPermissions) {
                const permissionsResponse = await this._managementApiService.getUserPermissions().toPromise();
                if (!permissionsResponse.success) {
                    if (this.errorPopupCount < 1) this.errorModelPopup(permissionsResponse.message);
                    this.errorPopupCount++;
                    return false;
                }
                // else {
                //     if (!this.validateSelectedOrgAndInstanceAccess(this.userData)) {
                //         return false;
                //     }
                // }
                this.userPermissions = permissionsResponse.response;
            }

            return this.checkUserPermissionOnRoute(path, action);
        } catch (error) {
            if (this.errorPopupCount < 1) this.errorModelPopup('An unexpected error occurred');
            this.errorPopupCount++;
            return false;
        }
    }

    async refreshProfileData() {
        const token = localStorage.getItem('msal.accessToken');
        const decodedToken: any = jwt_decode(token);
        const userDetailResponse = await this._managementApiService.getFullUserDetail(decodedToken.oid).toPromise();
        if (!userDetailResponse.success) {
            if (this.errorPopupCount < 1) this.errorModelPopup(userDetailResponse.message);
            this.errorPopupCount++;
            return false;
        }
        this.userData = userDetailResponse.response;
    }

    errorModelPopup(errorMessage) {
        let dialogRef = this.modalService.open(ErrorDialogComponent, {
            size: 'sm',
        });
        dialogRef.componentInstance.message = errorMessage;
        dialogRef.result
            .then((resp) => {
                if (resp) {
                    if (errorMessage == 'Current user does not exist.') {
                        this.logout();
                    }
                }
            })
            .catch((e) => {
                console.warn(e);
            });
    }
    errorModelPopupLg(errorMessage, messageAlternative) {
        let dialogRef = this.modalService.open(ErrorDialogComponent, {
            size: 'lg',
        });
        dialogRef.componentInstance.message = errorMessage;
        dialogRef.componentInstance.messageAlternative = messageAlternative;
        dialogRef.result
            .then((resp) => {
                if (resp) {
                }
            })
            .catch((e) => {
                console.warn(e);
            });
    }

    errorModelPopupExpiry(errorMessage) {
        let dialogRef = this.modalService.open(ErrorDialogComponent, {
            size: 'sm',
            backdrop: 'static',
            keyboard: false,
        });
        dialogRef.componentInstance.message = errorMessage;
        dialogRef.result
            .then((resp) => {
                if (resp) {
                    const token = localStorage.getItem('msal.accessToken');
                    const decodedToken: any = jwt_decode(token);
                    if (decodedToken.oid) this.logout();
                }
            })
            .catch((e) => {
                console.warn(e);
            });
    }

    logout() {
        this.LogoutCurrentInstance();
        this.authService.logout();
        // this.globalEmittersService.initiateInterval.next(false); // * clear time
        localStorage.clear();
    }

    getUserSessionData(isActive: boolean = false) {
        let user: UserSessions = new UserSessions();
        user.userId = this.authService.instance.getActiveAccount().idTokenClaims.oid;
        user.isActive = isActive;
        user.loginTime = new Date();
        user.deviceId = this.getDeviceId();
        user.editedBy = this.authService.instance.getActiveAccount().idTokenClaims.oid;
        user.editedDate = new Date();
        return user;
    }

    LogoutCurrentInstance() {
        const userForLogout = this.getUserSessionData();
        this._managementApiService
            .logoutCurrentUser(userForLogout)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (res) => {
                    if (res) {
                    } else {
                        this.errorModelPopup(res);
                    }
                },
                () => {}
            );
    }

    getDeviceId() {
        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
            deviceId = this.genDeviceId.generateUUIDV4();
            localStorage.setItem('deviceId', deviceId);
        }
        return deviceId;
    }
}
